import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Sparkles,
  Brain,
  Scale,
  FileText,
  Route,
  Flame,
  Wrench,
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Bell,
  Calendar,
  User,
  Shield,
  Zap,
  Activity
} from 'lucide-react';
import api from '../services/api';
import { usePushNotifications } from '../hooks/usePushNotifications';

const AgentMobilePortal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { supported: pushSupported, subscribed: pushSubscribed, busy: pushBusy, toggle: togglePush } = usePushNotifications();
  const [stats, setStats] = useState({
    rondesAujourdhui: 0,
    incidentsOuverts: 0,
    prochainShift: null
  });
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAgentStats();
  }, []);

  const fetchAgentStats = async () => {
    try {
      // Récupérer les stats de l'agent
      const [rondesRes, shiftsRes] = await Promise.all([
        api.get('/rondes/today').catch(() => ({ data: { total: 0 } })),
        api.get('/shifts/me/upcoming').catch(() => ({ data: [] }))
      ]);
      
      setStats({
        rondesAujourdhui: rondesRes.data?.total || 0,
        incidentsOuverts: 0,
        prochainShift: shiftsRes.data?.[0] || null
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      icon: Sparkles,
      label: 'ALFA IA',
      description: 'Assistant hybride fédéré',
      path: '/dashboard/alfa-federated',
      gradient: 'from-indigo-600 to-purple-600',
      badge: '🧬 5 LLM'
    },
    {
      icon: Brain,
      label: 'SecurIA',
      description: 'IA avec mémoire',
      path: '/dashboard/securia',
      gradient: 'from-purple-600 to-pink-600',
      badge: '🧠'
    },
    {
      icon: Scale,
      label: 'Tribunal IA',
      description: 'Consensus 3 juges',
      path: '/dashboard/tribunal-ia',
      gradient: 'from-blue-600 to-cyan-600',
      badge: '⚖️'
    },
    {
      icon: Brain,
      label: 'IA Insights',
      description: 'Analyse intelligente',
      path: '/dashboard/ai-insights',
      gradient: 'from-green-600 to-teal-600',
      badge: '🤖'
    }
  ];

  const operationalActions = [
    {
      icon: FileText,
      label: 'Main Courante',
      description: 'Journal des événements',
      path: '/dashboard/main-courante',
      color: 'bg-blue-600'
    },
    {
      icon: Route,
      label: 'Rondes',
      description: 'Patrouilles et contrôles',
      path: '/dashboard/rondes',
      color: 'bg-green-600'
    },
    {
      icon: Flame,
      label: 'SSI Incendie',
      description: 'Sécurité incendie',
      path: '/dashboard/ssi',
      color: 'bg-red-600'
    },
    {
      icon: Wrench,
      label: 'Équipements',
      description: 'Gestion matériel',
      path: '/dashboard/ssi/equipements',
      color: 'bg-orange-600'
    }
  ];

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header Mobile */}
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-6 pb-20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white/70 text-sm">Bienvenue,</p>
              <h1 className="text-xl font-bold text-white">{user?.name || 'Agent'}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className={`text-white hover:bg-white/20 ${pushSubscribed ? 'bg-emerald-500/30' : ''}`}
              onClick={togglePush}
              disabled={!pushSupported || pushBusy}
              title={pushSubscribed ? 'Notifications activées - cliquer pour désactiver' : 'Activer les notifications push'}
              data-testid="push-toggle-btn"
            >
              <Bell className={`w-5 h-5 ${pushSubscribed ? 'text-emerald-300' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Time Display */}
        <div className="text-center mb-4">
          <p className="text-5xl font-bold text-white font-mono">{formatTime(currentTime)}</p>
          <p className="text-white/70 capitalize mt-1">{formatDate(currentTime)}</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <Route className="w-6 h-6 text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{stats.rondesAujourdhui}</p>
            <p className="text-xs text-white/70">Rondes</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <AlertTriangle className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{stats.incidentsOuverts}</p>
            <p className="text-xs text-white/70">Incidents</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <CheckCircle className="w-6 h-6 text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">OK</p>
            <p className="text-xs text-white/70">Statut</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 -mt-10">
        {/* AI Quick Actions */}
        <Card className="bg-slate-800/90 backdrop-blur border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h2 className="font-bold text-white">Intelligence Artificielle</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => navigate(action.path)}
                  className={`p-4 rounded-xl bg-gradient-to-br ${action.gradient} hover:opacity-90 transition-all text-left`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <action.icon className="w-6 h-6 text-white" />
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded text-white">{action.badge}</span>
                  </div>
                  <p className="font-bold text-white">{action.label}</p>
                  <p className="text-xs text-white/70">{action.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Operational Actions */}
        <Card className="bg-slate-800/90 backdrop-blur border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-400" />
              <h2 className="font-bold text-white">Opérations</h2>
            </div>
            <div className="space-y-3">
              {operationalActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => navigate(action.path)}
                  className="w-full flex items-center gap-4 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-all"
                >
                  <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center`}>
                    <action.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-white">{action.label}</p>
                    <p className="text-sm text-slate-400">{action.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Shift Info */}
        {stats.prochainShift && (
          <Card className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-blue-700/50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-blue-400" />
                <h2 className="font-bold text-white">Prochain Shift</h2>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{stats.prochainShift.site || 'Site non défini'}</p>
                  <p className="text-sm text-blue-300">{stats.prochainShift.date} • {stats.prochainShift.startTime} - {stats.prochainShift.endTime}</p>
                </div>
                <div className="text-right">
                  <MapPin className="w-5 h-5 text-blue-400 inline" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* System Status */}
        <Card className="bg-slate-800/90 backdrop-blur border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">Système ALFA</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-green-400 text-sm">En ligne</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {['Mistral', 'Alpha-1', 'Alpha-2', 'Alpha-3', 'Alpha-4'].map((model, i) => (
                <div key={i} className="text-center">
                  <div className="w-8 h-8 mx-auto bg-slate-700 rounded-full flex items-center justify-center mb-1">
                    <Zap className="w-4 h-4 text-yellow-400" />
                  </div>
                  <p className="text-xs text-slate-400">{model}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-slate-500">SecurESSI-AI</p>
          <p className="text-xs text-slate-600">High Tech Security LTD • Secutor Pro LLC</p>
          <p className="text-xs text-slate-600">v3.0 PRO - Agent Mobile Portal</p>
        </div>
      </div>
    </div>
  );
};

export default AgentMobilePortal;
